/**
 * MeasuredMetrics.jsx — Phase 12 mandate #5
 *
 * Header strip inside the Customer Hub segments tab showing the 3
 * MEASURED metrics the team needs to trust:
 *   - Conversion rate (paid ÷ all customers)
 *   - Retention 30d (previous 30 → current 30)
 *   - AOV (avg paid invoice value)
 *
 * All numbers here are 100% from pos_sales. No projections. Visible to
 * the entire team by design (they are the trust-building metrics).
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { resolvePreset } from "../DateRangePicker";
import MetricBadge from "./MetricBadge";
import { GRAIN_PRESET } from "./grainPreset";
import { useCustomerFilter } from "./CustomerFilterContext";

const fmtNum = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
const fmtSar = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// يومي/أسبوعي/شهري — نفس أسلوب فلتر شاشة تحليل المبيعات (يوم تشغيلي 7ص).
const GRAINS = [
  { id: "day",   label: "يومي" },
  { id: "week",  label: "أسبوعي" },
  { id: "month", label: "شهري" },
];

export default function MeasuredMetrics({ windowDays = 90 }) {
  const shared = useCustomerFilter();
  const [grain, setGrain] = useState("week");
  const [localOverride, setLocalOverride] = useState(false);
  const [state, setState] = useState({ loading: true, err: null, forbidden: false, data: null });

  // الشريط المشترك يُصفّر التجاوز المحلي فتتبعه البطاقة.
  useEffect(() => {
    if (shared) setLocalOverride(false);
  }, [shared?.version]);

  const presetRange = resolvePreset(GRAIN_PRESET[grain]) || { from: "", to: "" };
  const range = (!localOverride && shared)
    ? { from: shared.from, to: shared.to }
    : presetRange;

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, err: null, forbidden: false, data: null });
    api.get("/customer-metrics/team-summary", { params: { from: range.from, to: range.to } })
      .then((r) => { if (!cancelled) setState({ loading: false, err: null, data: r.data }); })
      .catch((e) => {
        if (cancelled) return;
        // PROD-FIX (تدقيق 2026-09-29) — 403 = لا يملك الصلاحية (رسالة واضحة
        // بلا كلمة "خطأ")، غير ذلك يبقى نص الخادم كما هو.
        const forbidden = e?.response?.status === 403;
        const msg = forbidden
          ? "هذه البطاقة غير متاحة لدورك — اطلب الصلاحية من المدير"
          : (e?.response?.data?.detail || e?.message || "خطأ");
        setState({ loading: false, err: msg, forbidden, data: null });
      });
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const d = state.data || {};

  const cards = [
    {
      id:      "conversion_rate_paid",
      title:   "نسبة الشراء المدفوع",
      value:   d.conversion_rate_pct != null ? `${fmtNum(d.conversion_rate_pct)}%` : "—",
      hint:    `${fmtSar(d.unique_customers_paid)} من ${fmtSar(d.unique_customers_all)} عميل فريد دفع فعلًا`,
      color:   "#0F766E",
      testid:  "measured-conversion",
    },
    {
      id:      "retention_30d",
      title:   "معدّل الاحتفاظ",
      value:   d.retention_30d_pct != null ? `${fmtNum(d.retention_30d_pct)}%` : "—",
      hint:    `${fmtSar(d.retention_returned)} من ${fmtSar(d.retention_cohort_size)} عميل عادوا من الفترة السابقة المساوية`,
      color:   "#0284C7",
      testid:  "measured-retention",
    },
    {
      id:      "aov_paid_window",
      title:   "متوسط قيمة الفاتورة",
      value:   d.aov_sar != null ? `${fmtSar(d.aov_sar)} ر` : "—",
      hint:    `${fmtSar(d.invoice_count)} فاتورة · ${fmtSar(d.total_sar)} ر مجموع`,
      color:   "#D97706",
      testid:  "measured-aov",
    },
  ];

  return (
    <div dir="rtl" data-testid="measured-metrics-wrap"
         style={{ marginBottom: 20,
                  fontFamily: "'El Messiri', -apple-system, 'Noto Naskh Arabic', sans-serif" }}>
      {/* أزرار يومي/أسبوعي/شهري (يوم تشغيلي 7ص) — تُعيد حساب المؤشرات الثلاثة */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#78716C", fontWeight: 700 }}>
          الفترة (يوم تشغيلي 7ص → 6:59ص):
        </span>
        <div data-testid="measured-grain"
             style={{ display: "flex", background: "rgba(15,118,110,0.08)", borderRadius: 10, padding: 2 }}>
          {GRAINS.map((g) => (
            <button key={g.id} onClick={() => { setGrain(g.id); setLocalOverride(true); }}
                    data-testid={`measured-grain-${g.id}`}
                    style={{
                      padding: "6px 14px", borderRadius: 8, border: "none",
                      cursor: "pointer", fontSize: 13,
                      fontWeight: (localOverride ? grain === g.id : (shared ? (shared.grain === g.id && !shared.manualActive) : grain === g.id)) ? 800 : 600,
                      background: (localOverride ? grain === g.id : (shared ? (shared.grain === g.id && !shared.manualActive) : grain === g.id)) ? "#fff" : "transparent",
                      color: (localOverride ? grain === g.id : (shared ? (shared.grain === g.id && !shared.manualActive) : grain === g.id)) ? "#0F766E" : "#78716C",
                      boxShadow: (localOverride ? grain === g.id : (shared ? (shared.grain === g.id && !shared.manualActive) : grain === g.id)) ? "0 1px 3px rgba(15,118,110,0.15)" : "none",
                      transition: "background 0.15s ease",
                    }}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <section
        dir="rtl"
        data-testid="measured-metrics-root"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          fontVariantNumeric: "lining-nums tabular-nums",
        }}
      >
      {cards.map((c) => (
        <article
          key={c.id}
          data-testid={c.testid}
          style={{
            background: "#FFFFFF",
            borderRadius: 16,
            padding: "18px 20px",
            border: "1px solid rgba(61,42,28,0.12)",
            boxShadow: "0 4px 16px rgba(61,42,28,0.06)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#57534E" }}>
              {c.title}
            </div>
            <MetricBadge metricId={c.id} size="xs" />
          </div>
          <div style={{
            fontSize: 26,
            fontWeight: 700,
            color: c.color,
            lineHeight: 1.1,
            letterSpacing: -0.5,
          }}>
            {state.loading ? "…" : c.value}
          </div>
          <div style={{ fontSize: 11, color: "#78716C", lineHeight: 1.5 }}
               data-testid={`${c.testid}-hint`}>
            {state.err ? (state.forbidden ? state.err : `خطأ: ${state.err}`) : c.hint}
          </div>
        </article>
      ))}
      </section>
    </div>
  );
}
